import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import BlogHero from './components/BlogHero';
import BlogSidebar from './components/BlogSidebar';
import InThisPost, { generateHeadingIds } from './components/InThisPost';
import CommentSection from './components/CommentSection';
import BlogFAQ from './components/BlogFAQ';
import BlogCTABar from './components/BlogCTABar';
import { supabase } from '@/lib/supabase';
import { useSEO } from '@/utils/seo';
import { usePharmacy } from '@/context/PharmacyContext';

interface BlogPostData {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image_url: string;
  author: string;
  category: string;
  published_at: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  faq_data?: { question: string; answer: string }[];
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { settings } = usePharmacy();
  const [isScrolled, setIsScrolled] = useState(false);
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [recentPosts, setRecentPosts] = useState<{ title: string; slug: string; published_at: string }[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;
      const { data } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .single();
      setPost(data);
      setLoading(false);
    };

    const fetchRecent = async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('title, slug, published_at')
        .eq('is_published', true)
        .neq('slug', slug || '')
        .order('published_at', { ascending: false })
        .limit(5);
      setRecentPosts(data || []);
    };

    fetchPost();
    fetchRecent();
  }, [slug]);

  // Process content: generate heading IDs
  const processedContent = useMemo(() => {
    if (!post?.content) return '';
    return generateHeadingIds(post.content);
  }, [post?.content]);

  // Parse FAQ data
  const faqs = useMemo(() => {
    if (!post?.faq_data || !Array.isArray(post.faq_data) || post.faq_data.length === 0) return [];
    return post.faq_data;
  }, [post?.faq_data]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thornhilltreatment.com';
  const pageUrl = `${siteUrl}/blog/${slug}`;

  // Build FAQPage schema if FAQs exist
  const faqSchema = useMemo(() => {
    if (faqs.length === 0) return undefined;
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };
  }, [faqs]);

  useSEO({
    title: post?.meta_title || post?.title || 'Blog Post',
    description: post?.meta_description || post?.excerpt || '',
    keywords: post?.meta_keywords || 'addiction treatment, pharmacy, Thornhill',
    canonical: pageUrl,
    ogType: 'article',
    ogImage: post?.featured_image_url || '',
    schema: faqSchema || (post ? {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.excerpt,
      image: post.featured_image_url,
      author: { '@type': 'Organization', name: post.author },
      publisher: {
        '@type': 'Organization',
        name: settings?.pharmacy_name || 'Thornhill Drug-Mart',
        logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/621fe86c950ded9745dd2c19cd7c5595/c0b5a15a2b815315bfd2ccbd2c453721.png' },
      },
      datePublished: post.published_at,
      dateModified: post.published_at,
      mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
    } : undefined),
  });

  const shareUrl = encodeURIComponent(pageUrl);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar isScrolled={false} />
        <div className="max-w-7xl mx-auto px-6 py-32 text-center">
          <div className="w-16 h-16 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-950 mb-2">Post Not Found</h1>
          <p className="text-foreground-500 mb-6">This article may have been removed or is no longer available.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold px-6 py-2.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const date = new Date(post.published_at).toLocaleDateString('en-CA', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar isScrolled={isScrolled} />

      <BlogHero
        title={post.title}
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Blog', href: '/blog' },
          { label: post.title },
        ]}
      />

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-10 items-start">

          {/* Main content */}
          <main className="flex-1 min-w-0">
            {/* Meta bar */}
            <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-background-200">
              <span className="text-xs bg-accent-100 text-accent-700 px-3 py-1 rounded-full font-medium">{post.category}</span>
              <span className="text-sm text-foreground-500 flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {date}
              </span>
              <span className="text-sm text-foreground-500 flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {post.author}
              </span>
            </div>

            {/* Featured image */}
            {post.featured_image_url && (
              <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8 bg-background-100">
                <img
                  src={post.featured_image_url}
                  alt={post.title}
                  className="w-full h-full object-cover"
                  width={1200}
                  height={675}
                />
              </div>
            )}

            {/* Content with auto-generated heading IDs */}
            <article className="prose prose-sm md:prose-base max-w-none mb-8" ref={contentRef}>
              <div
                dangerouslySetInnerHTML={{ __html: processedContent }}
                className="text-foreground-700 leading-relaxed space-y-4
                  [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:scroll-mt-28
                  [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:scroll-mt-28
                  [&_h4]:text-base [&_h4]:font-semibold [&_h4]:text-foreground-900 [&_h4]:mt-6 [&_h4]:mb-2
                  [&_p]:text-foreground-700 [&_p]:leading-relaxed
                  [&_a]:text-primary-600 [&_a]:underline [&_a]:underline-offset-2
                  [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1
                  [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1
                  [&_li]:text-foreground-700
                  [&_blockquote]:border-l-4 [&_blockquote]:border-primary-500 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-foreground-600
                  [&_strong]:font-bold [&_strong]:text-foreground-900
                  [&_table]:w-full [&_table]:border-collapse [&_table]:my-4
                  [&_th]:border [&_th]:border-background-300 [&_th]:px-3 [&_th]:py-2 [&_th]:bg-background-50 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold
                  [&_td]:border [&_td]:border-background-300 [&_td]:px-3 [&_td]:py-2 [&_td]:text-sm
                  [&_img]:rounded-lg [&_img]:my-4
                  [&_pre]:bg-foreground-900 [&_pre]:text-foreground-100 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-4
                  [&_code]:bg-background-100 [&_code]:px-1 [&_code]:rounded [&_code]:text-sm
                  [&_pre_code]:bg-transparent [&_pre_code]:text-inherit [&_pre_code]:p-0
                  [&_hr]:border-background-200 [&_hr]:my-8
                "
              />
            </article>

            {/* Share buttons */}
            <div className="flex items-center gap-3 py-6 border-t border-background-200">
              <span className="text-sm font-semibold text-foreground-700">Share:</span>
              <a
                href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-[#1DA1F2] text-white rounded-lg flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer"
                aria-label="Share on Twitter"
              >
                <i className="ri-twitter-x-line"></i>
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-[#1877F2] text-white rounded-lg flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer"
                aria-label="Share on Facebook"
              >
                <i className="ri-facebook-fill"></i>
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 bg-[#0A66C2] text-white rounded-lg flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer"
                aria-label="Share on LinkedIn"
              >
                <i className="ri-linkedin-fill"></i>
              </a>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(pageUrl);
                }}
                className="w-9 h-9 bg-foreground-700 text-white rounded-lg flex items-center justify-center hover:bg-foreground-800 transition-colors cursor-pointer text-xs"
                aria-label="Copy link"
                title="Copy link"
              >
                <i className="ri-link"></i>
              </button>
            </div>

            {/* Comments */}
            <CommentSection postId={post.id} />

            {/* FAQ (from database) */}
            {faqs.length > 0 && (
              <>
                {/* Mark FAQ section for InThisPost to pick up */}
                <div id="frequently-asked-questions" className="scroll-mt-28" />
                <BlogFAQ faqs={faqs} />
              </>
            )}
          </main>

          {/* Sticky sidebar with InThisPost at top */}
          <BlogSidebar
            recentPosts={recentPosts}
            inThisPostContent={processedContent}
          />

        </div>
      </div>

      <BlogCTABar />
      <Footer />
    </div>
  );
}